import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import Switch from 'react-switch'

import Header from 'components/headers/bulletins'
import { getWebPushConfig, updateWebPushPrefs } from 'services/webPush'
import {
    disableWebPush,
    EnableStage,
    enableWebPush,
    isWebPushOn,
    notificationPermission,
    pushAvailability,
} from 'modules/webPush'
import { queueEvent } from 'utilities/analytics'
import WebPushSelfTest from './selfTest'

const CONFIG_KEY = ['web-push-config']

// Said under the switch while a tap is in flight: turning on can take half a
// minute on a browser's first visit, and a greyed-out switch alone reads as
// «nothing happens».
const STAGE_TEXT: Record<EnableStage, string> = {
    permission:
        'مرورگر برای اعلان اجازه می‌خواهد؛ «Allow» یا «اجازه» را بزنید. اگر پنجره‌ای نمی‌بینید، روی آیکون کنار نشانی سایت بزنید.',
    preparing:
        'در حال روشن کردن اعلان روی این مرورگر… بار اول ممکن است تا نیم دقیقه طول بکشد.',
}

const CATEGORIES: Array<{
    key: WebPushCategory
    title: string
    hint: string
}> = [
    {
        key: 'reminders',
        title: 'یادآوری پرداخت',
        hint: 'نزدیک سررسید یا بعد از عقب افتادن حق عضویت و قسط وام.',
    },
    {
        key: 'approvals',
        title: 'درخواست‌ها و تأییدها',
        hint: 'دعوت به صندوق، ضمانت وام، و درخواست وام، برداشت یا خروجی که منتظر بررسی شماست.',
    },
    {
        key: 'receipts',
        title: 'رسید پرداخت‌ها',
        hint: 'پرداخت، برداشت یا وامی که به نام شما ثبت شد.',
    },
    {
        key: 'fund_updates',
        title: 'خبرهای صندوق',
        hint: 'عضو جدید، قرعه‌کشی و برنده‌اش، و گزارش وام‌های صندوق.',
    },
    {
        key: 'weekly',
        title: 'پیگیری هفتگی',
        hint: 'هفته‌ای یک بار، فقط اگر کاری منتظر شماست.',
    },
]

const switchProps = {
    onColor: '#75B844',
    offColor: '#B6BCC8',
    onHandleColor: '#EAEEF0',
    handleDiameter: 25,
    uncheckedIcon: false as const,
    checkedIcon: false as const,
    boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.6)',
    activeBoxShadow: '0px 1px 5px rgba(0, 0, 0, 0.6)',
    height: 30,
    width: 60,
}

/** Why this browser cannot be switched on, in words a member can act on. */
const blockedReason = (
    availability: ReturnType<typeof pushAvailability>,
    permission: NotificationPermission | null,
    serverEnabled: boolean | undefined
): string | null => {
    if (availability === 'miniapp')
        return 'داخل بله، تلگرام و ایتا اعلان وب نمی‌رسد. برای دریافت اعلان، همیان را در مرورگر گوشی باز کنید: pwa.hamyanapp.com'
    if (availability === 'ios_browser')
        return 'در آیفون و آیپد، اعلان فقط وقتی می‌رسد که همیان روی صفحهٔ اصلی باشد: در Safari دکمهٔ اشتراک‌گذاری و بعد «Add to Home Screen» را بزنید و همیان را از همان آیکون باز کنید.'
    if (availability === 'unsupported')
        return 'این مرورگر اعلان وب را پشتیبانی نمی‌کند. نسخهٔ تازهٔ Chrome، Edge یا Firefox را امتحان کنید.'
    if (permission === 'denied')
        return 'اعلان‌های همیان در تنظیمات مرورگر مسدود شده‌اند. از آیکون کنار نشانی سایت، «اعلان‌ها» را روی «اجازه» بگذارید و صفحه را دوباره باز کنید.'
    if (serverEnabled === false)
        return 'اعلان وب همیان موقتاً در دسترس نیست.'
    return null
}

export default function NotificationSettings() {
    const queryClient = useQueryClient()
    const availability = pushAvailability()
    const [permission, setPermission] = useState(notificationPermission())
    const [deviceOn, setDeviceOn] = useState(isWebPushOn())
    const [busy, setBusy] = useState(false)
    const [stage, setStage] = useState<EnableStage | null>(null)
    const viewedTracked = useRef(false)

    const { data: config, isLoading, isError, refetch } = useQuery(
        CONFIG_KEY,
        getWebPushConfig,
        { staleTime: 60_000, retry: 1 }
    )

    useEffect(() => {
        if (viewedTracked.current || isLoading) return
        viewedTracked.current = true
        queueEvent('web_push_settings_viewed', {
            availability,
            permission,
            device_on: deviceOn,
            active_devices: config?.active_devices ?? null,
        })
        // Once, when the first answer is in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading])

    const blocked = blockedReason(availability, permission, config?.enabled)

    const toggleDevice = async () => {
        if (busy) return
        setBusy(true)
        try {
            if (deviceOn) {
                const ok = await disableWebPush()
                if (ok) {
                    setDeviceOn(false)
                    toast.info('اعلان‌ها روی این دستگاه خاموش شد.')
                } else {
                    toast.error('خاموش کردن اعلان‌ها انجام نشد. دوباره امتحان کنید.')
                }
            } else {
                const result = await enableWebPush('settings', setStage)
                setPermission(notificationPermission())
                if (result === 'subscribed') {
                    setDeviceOn(true)
                    toast.success('اعلان‌ها روی این دستگاه روشن شد.')
                } else if (result === 'failed') {
                    toast.error('روشن کردن اعلان‌ها انجام نشد. کمی بعد دوباره امتحان کنید.')
                }
                // denied / dismissed / unavailable explain themselves on the page.
            }
        } finally {
            // Whatever happened, the switch must not stay stuck grey.
            setBusy(false)
            setStage(null)
            queryClient.invalidateQueries(CONFIG_KEY)
        }
    }

    const togglePref = async (category: WebPushCategory) => {
        if (!config) return
        const enabled = !config.prefs[category]
        const previous = config
        queryClient.setQueryData<IWebPushConfig>(CONFIG_KEY, {
            ...config,
            prefs: { ...config.prefs, [category]: enabled },
        })
        try {
            const prefs = await updateWebPushPrefs({ [category]: enabled })
            queryClient.setQueryData<IWebPushConfig>(CONFIG_KEY, (current) => ({
                ...(current ?? previous),
                prefs,
            }))
            queueEvent('web_push_pref_changed', { category, enabled })
        } catch {
            queryClient.setQueryData(CONFIG_KEY, previous)
            toast.error('تغییر ذخیره نشد. دوباره امتحان کنید.')
        }
    }

    // The categories are per person, not per browser: they apply to every
    // browser this person has switched on. Worth showing whenever at least one
    // of them is on, even if it is not this one.
    const showCategories =
        !!config?.enabled && (deviceOn || config.active_devices > 0)

    return (
        <div className="flex flex-col bg-canvas min-h-screen">
            <Header title="اعلان‌ها" />

            <div className="px-4 py-4 flex flex-col gap-4">
                <section className="bg-white rounded-lg shadow-header p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-base font-bold">
                                اعلان روی این دستگاه
                            </p>
                            <p className="text-xs text-text-secondary mt-1 leading-6">
                                همان کارهای مهمی که اپ همیان خبر می‌دهد، در
                                مرورگر هم به شما می‌رسد.
                            </p>
                        </div>
                        <Switch
                            {...switchProps}
                            onChange={toggleDevice}
                            checked={deviceOn}
                            disabled={
                                busy ||
                                isLoading ||
                                (!deviceOn && !!blocked)
                            }
                            aria-label="اعلان روی این دستگاه"
                        />
                    </div>
                    {busy && stage && (
                        <p
                            className="mt-3 text-xs leading-6 rounded-md bg-canvas p-3"
                            aria-live="polite"
                        >
                            {STAGE_TEXT[stage]}
                        </p>
                    )}
                    {blocked && !deviceOn && (
                        <p className="mt-3 text-xs leading-6 rounded-md bg-warning-soft text-warning-on p-3">
                            {blocked}
                        </p>
                    )}
                    {isError && (
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="mt-3 text-xs text-brand-deep underline"
                        >
                            تنظیمات بارگیری نشد؛ دوباره امتحان کنید
                        </button>
                    )}
                </section>

                {showCategories && config && (
                    <section className="bg-white rounded-lg shadow-header p-4">
                        <p className="text-base font-bold mb-1">
                            چه چیزهایی خبر بدهیم؟
                        </p>
                        {CATEGORIES.map(({ key, title, hint }) => (
                            <div
                                key={key}
                                className="flex items-center justify-between gap-3 py-3 border-b border-divider last:border-b-0"
                            >
                                <div className="min-w-0">
                                    <p className="text-sm">{title}</p>
                                    <p className="text-xs text-text-secondary mt-1 leading-5">
                                        {hint}
                                    </p>
                                </div>
                                <Switch
                                    {...switchProps}
                                    onChange={() => togglePref(key)}
                                    checked={!!config.prefs[key]}
                                    aria-label={title}
                                />
                            </div>
                        ))}
                    </section>
                )}

                {deviceOn && config?.enabled && <WebPushSelfTest />}

                <p className="text-xs text-text-secondary leading-6 px-1">
                    برای اینکه مزاحم نشویم: از ساعت ۲۲ تا ۸ صبح فقط رسید
                    پرداخت‌ها، آن هم بی‌صدا، می‌رسد و بقیه تا صبح صبر می‌کنند.
                    از هر نوع اعلان هم در روز تعداد محدودی می‌فرستیم و یک خبر را
                    دو بار نمی‌فرستیم.
                </p>
            </div>
        </div>
    )
}
